import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import Box from "@galaxy-io/dls/layout/Box";
import Tabs from "@galaxy-io/dls/navigation/Tabs";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import {
  type ExclusionReason,
  exclusionFields,
  exclusionKindLabels,
  exclusionKindSchema,
  exclusionOperators,
} from "@gtm/contracts/core/exclusions";
import { styled } from "@linaria/react";
import { PlusIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Fragment, type ReactNode } from "react";
import { useUrlState } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FilterForm } from "@/components/workspace/filter-form";
import { PageHeader } from "@/components/workspace/page-header";
import { FeedViewport } from "@/components/workspace/people-styles";
import { RecordTable } from "@/components/workspace/record-table";
import { SelectField } from "@/components/workspace/select-field";
import { Status } from "@/components/workspace/status";
import { useTableSort } from "@/components/workspace/use-table-sort";
import { apiClient, unwrap } from "@/lib/api-client";
import { date } from "@/lib/utils";

/**
 * A record can match several rules, so the reason columns stack one line per
 * reason and stay aligned across the row.
 */
const Lines = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[2]};
  min-width: 0;
`;
// A padded control would sit indented against the plain text beside it, so the
// link to a rule is a bare button that reads as ordinary cell text.

function Reasons({
  reasons,
  children,
}: {
  reasons: ExclusionReason[];
  children: (reason: ExclusionReason) => ReactNode;
}) {
  if (!reasons.length) return null;
  return (
    <Lines>
      {reasons.map((reason, index) => (
        <Fragment key={`${reason.ruleId}:${reason.viaId}:${index}`}>{children(reason)}</Fragment>
      ))}
    </Lines>
  );
}
function Cell({ children }: { children: ReactNode }) {
  return (
    <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY} lineClamp={1}>
      {children}
    </Text>
  );
}
export function ExclusionsView() {
  const { params, update } = useUrlState();
  const records = params.get("view") === "records",
    q = params.get("q") || "",
    page = Math.max(1, Number(params.get("page")) || 1);
  const kind = exclusionKindSchema.safeParse(params.get("kind")).data;
  const ruleId = params.get("ruleId") || undefined;
  const sort = useTableSort(records ? (["name", "kind"] as const) : (["name", "updated"] as const));
  const query = { q, page, ...sort.query };
  const rules = useQuery({
    queryKey: ["exclusion-rules", query],
    enabled: !records,
    queryFn: () => unwrap(apiClient().GET("/exclusions/rules", { params: { query } })),
  });
  const excluded = useQuery({
    queryKey: ["excluded-records", query, kind, ruleId],
    enabled: records && !!ruleId,
    queryFn: () =>
      unwrap(
        apiClient().GET("/exclusions/records", {
          params: { query: { ...query, kind, ruleId: ruleId! } },
        }),
      ),
  });
  const filterRule = useQuery({
    queryKey: ["exclusion-rule", ruleId],
    enabled: !!ruleId,
    queryFn: () =>
      unwrap(
        apiClient().GET("/exclusions/rules/{id}", {
          params: { path: { id: ruleId! } },
        }),
      ),
  });
  const result = records ? excluded : rules;
  const paging = {
    page,
    total: result.data?.total,
    hasPrevious: page > 1,
    hasNext: page * 40 < (result.data?.total || 0),
    busy: result.isFetching || !!result.error,
    onPrevious: () => update({ page: String(page - 1) }),
    onNext: () => update({ page: String(page + 1) }),
  };
  return (
    <>
      <PageHeader
        title="Exclusions"
        actions={
          <Button label="New rule" icon={PlusIcon} onClick={() => update({ exclusion: "new" })} />
        }
      />
      <Box padding={[0, 12]}>
        <Tabs
          ariaLabel="Exclusion sections"
          value={records ? "records" : "rules"}
          onChange={(value) =>
            update({
              view: value === "records" ? "records" : null,
              page: null,
              sort: null,
              direction: null,
              q: null,
              kind: null,
              ruleId: null,
            })
          }
          items={[
            { id: "rules", label: "Rules" },
            { id: "records", label: "Excluded" },
          ]}
        />
      </Box>
      <FilterForm
        onSubmit={(e) => {
          e.preventDefault();
          update({
            q: String(new FormData(e.currentTarget).get("q") || "") || null,
            page: null,
          });
        }}
      >
        <Input
          key={q}
          name="q"
          defaultValue={q}
          placeholder={records ? "Search excluded records…" : "Search rules…"}
          aria-label={records ? "Search excluded records" : "Search exclusion rules"}
          type="search"
          size={InputSize.SMALL}
          label={records ? "Search excluded records" : "Search exclusion rules"}
          hideLabel
        />
        {records && (
          <SelectField
            compact
            label="Record type"
            value={kind || ""}
            options={[
              { value: "", label: "All records" },
              { value: "person", label: "People" },
              { value: "company", label: "Companies" },
              { value: "activity", label: "Signals" },
              { value: "identity", label: "Accounts" },
            ]}
            onChange={(kind) => update({ kind: kind || null, page: null })}
            hideLabel
          />
        )}
        {ruleId && (
          <Button
            label={`Change rule: ${filterRule.data?.name || "Selected rule"}`}
            variant={ButtonVariant.SECONDARY}
            onClick={() => update({ view: null, ruleId: null, page: null, q: null })}
            size={ButtonSize.SMALL}
          />
        )}
        <Button type="submit" label="Search" size={ButtonSize.SMALL} />
      </FilterForm>
      <FeedViewport>
        {records && !ruleId ? (
          <Box padding={12}>
            <Text>Select a rule from Rules to view its excluded records.</Text>
            <Button
              label="Choose rule"
              onClick={() => update({ view: null, page: null, q: null })}
            />
          </Box>
        ) : records ? (
          <RecordTable
            label="Excluded records"
            sort={sort}
            isLoading={excluded.isPending || excluded.isFetching}
            error={excluded.error || undefined}
            onRetry={() => void excluded.refetch()}
            paging={paging}
            rowKeys={(excluded.data?.items || []).map((row) => `${row.kind}:${row.id}`)}
            onRowClick={(key) => {
              const [type, id] = key.split(":");
              if (type === "identity") {
                const row = excluded.data?.items.find((r) => r.id === id);
                update({ exclusion: row?.reasons[0]?.ruleId || null });
              } else
                update({
                  personId: null,
                  companyId: null,
                  activityId: null,
                  [type === "activity" ? "activityId" : `${type}Id`]:
                    type === "activity" ? `observation:${id}` : id,
                });
            }}
            columns={[
              { key: "name", label: "Record", width: 220, pin: "left" },
              { key: "kind", label: "Type", width: 110 },
              { key: "context", label: "Company / provider", width: 180 },
              { key: "rule", label: "Excluded by", width: 190 },
              { key: "via", label: "Inherited via", width: 150 },
              { key: "field", label: "Matched on", width: 160 },
              { key: "value", label: "Value", width: "auto" },
            ]}
            rows={(excluded.data?.items || []).map((row) => ({
              name: row.name,
              kind: exclusionKindLabels[row.kind],
              context: row.context,
              rule: (
                <Reasons reasons={row.reasons}>
                  {(reason) => (
                    <Button
                      label={reason.name}
                      variant={ButtonVariant.TERTIARY}
                      size={ButtonSize.SMALL}
                      onClick={() => update({ exclusion: reason.ruleId })}
                    />
                  )}
                </Reasons>
              ),
              via: (
                <Reasons reasons={row.reasons}>
                  {(reason) => <Cell>{reason.viaName || "—"}</Cell>}
                </Reasons>
              ),
              field: (
                <Reasons reasons={row.reasons}>
                  {(reason) => (
                    <Cell>
                      {reason.field === "record"
                        ? "Exact record"
                        : (exclusionFields[reason.field as keyof typeof exclusionFields] ??
                          reason.field)}
                    </Cell>
                  )}
                </Reasons>
              ),
              value: (
                <Reasons reasons={row.reasons}>{(reason) => <Cell>{reason.value}</Cell>}</Reasons>
              ),
            }))}
          />
        ) : (
          <RecordTable
            label="Exclusion rules"
            sort={sort}
            isLoading={rules.isPending || rules.isFetching}
            error={rules.error || undefined}
            onRetry={() => void rules.refetch()}
            paging={paging}
            rowKeys={(rules.data?.items || []).map((r) => r.id)}
            onRowClick={(id) => update({ exclusion: id })}
            columns={[
              { key: "name", label: "Rule", width: 180, pin: "left" },
              { key: "condition", label: "Condition", width: "auto" },
              { key: "state", label: "State", width: 110 },
              { key: "affected", label: "Excluded records", width: 210 },
              { key: "creator", label: "Created by", width: 150 },
              { key: "updated", label: "Updated", width: 190 },
            ]}
            rows={(rules.data?.items || []).map((r) => ({
              name: r.name,
              condition: r.field
                ? `${exclusionFields[r.field as keyof typeof exclusionFields]} ${exclusionOperators[r.operator as keyof typeof exclusionOperators]?.toLowerCase()} “${r.value}”${r.provider ? ` · ${r.provider}` : ""}`
                : r.companyId
                  ? "Exact company and associated records"
                  : r.personId
                    ? "Exact person and their signals"
                    : "Exact provider account",
              state: (
                <Status
                  value={r.enabled ? "active" : "paused"}
                  label={r.enabled ? "Enabled" : "Paused"}
                />
              ),
              affected: (
                <Button
                  label="View excluded records"
                  variant={ButtonVariant.TERTIARY}
                  onClick={() =>
                    update({ view: "records", ruleId: r.id, page: null, q: null, kind: null })
                  }
                />
              ),
              creator: r.createdByName,
              updated: date(r.updatedAt, true),
            }))}
          />
        )}
      </FeedViewport>
    </>
  );
}
